import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Label,
  Separator,
} from 'platform/components';
import {Box, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useState} from 'react';
import {DeepPartial} from 'react-hook-form';

import {uniq} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetAiLeadSettingsQuery, useUpdateAiLeadSettingsMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError, useBranches, useGetCurrentBranch} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const aiLeadConfigurationFormSchema = z.object({
  defaultBranchId: z.string(),
  allowedEmailAddresses: z
    .array(z.string())
    .optional()
    .refine((emails) => isNilOrEmpty(emails) || emails?.every(validateEmail), {
      message: i18n.t('errors.invalid_string.email'),
    }),
});

export type AiLeadConfigurationFormType = z.infer<typeof aiLeadConfigurationFormSchema>;

interface AiLeadConfigurationFormProps extends RequiredTestIdProps {}

export function AiLeadConfigurationForm(props: AiLeadConfigurationFormProps) {
  const {data: aiLeadSettings} = useGetAiLeadSettingsQuery();
  const {branchOptions} = useBranches();
  const {activeBranchId} = useGetCurrentBranch();
  const [updateAiLeadSettings] = useUpdateAiLeadSettingsMutation();

  const [emailOptions, setEmailOptions] = useState<string[]>([]);

  const getAllEmailOptions = () => {
    const existingOptions = aiLeadSettings?.allowedEmailAddresses ?? [];
    const combinedEmails = [...existingOptions, ...emailOptions];

    return uniq(combinedEmails).filter(Boolean);
  };

  const defaultValues: DeepPartial<AiLeadConfigurationFormType> = {
    allowedEmailAddresses: aiLeadSettings?.allowedEmailAddresses ?? [],
    defaultBranchId: aiLeadSettings?.defaultBranchId ?? activeBranchId ?? undefined,
  };

  const handleSubmit: FormSubmitHandler<AiLeadConfigurationFormType> = async (values) => {
    await updateAiLeadSettings({
      enabled: true,
      allowedEmailAddresses: values.allowedEmailAddresses ?? [],
      defaultBranchId: values.defaultBranchId,
    })
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);
  };

  return (
    <Form<AiLeadConfigurationFormType>
      onSubmit={handleSubmit}
      experimentalZodSchema={aiLeadConfigurationFormSchema}
      defaultValues={defaultValues}
      data-testid={suffixTestId('leadConfigurationForm', props)}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <Text size="small" color="secondary" data-testid={suffixTestId('description', props)}>
            {i18n.t('page.settings.labels.aiLeadManagementEnabledDescription')}
          </Text>

          <VStack>
            <Label data-testid={suffixTestId('processingEmailLabel', props)}>
              {i18n.t('page.settings.labels.ProcessingEmailAddress')}
            </Label>

            <Box
              paddingVertical={1}
              paddingHorizontal={2}
              borderRadius="small"
              border="1px solid"
              borderColor="general.separator"
              width="fit-content"
              minWidth="50%"
              data-testid={suffixTestId('processingEmailValue', props)}
            >
              {aiLeadSettings?.processingEmailAddress ?? ''}
            </Box>
          </VStack>

          <Separator spacing={0} />

          <FormField
            control={control}
            name="defaultBranchId"
            type="choice"
            options={branchOptions}
            label={i18n.t('entity.branch.labels.defaultBranch')}
            menuInPortal
            data-testid={suffixTestId('defaultBranchId', props)}
          />

          <FormField
            control={control}
            name="allowedEmailAddresses"
            menuInPortal
            type="creatableMultiChoice"
            label={i18n.t('page.settings.labels.AllowedEmailAddresses')}
            options={getAllEmailOptions().map((email) => ({label: email, value: email}))}
            noOptionsMessage={() => i18n.t('page.generalSettings.labels.typeToCreate')}
            onCreateOption={(inputValue) => {
              const currentEmails = formApi.getValues('allowedEmailAddresses');
              formApi.setValue(
                'allowedEmailAddresses',
                uniq([...(currentEmails ?? []), inputValue])
              );

              setEmailOptions((prev) => uniq([...prev, inputValue]));
            }}
            data-testid={suffixTestId('allowedEmailAddresses', props)}
          />

          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.cancel')}
              variant="secondary"
              onClick={closeCurrentDialog}
              data-testid={suffixTestId('cancel', props)}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('general.actions.save')}
              data-testid={suffixTestId('saveButton', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
